'use client'

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/styles'
import { SCROLL_PROGRESS_TEST_IDS } from '@/lib/test-ids'

interface Props {
  containerRef?: React.RefObject<HTMLDivElement | null>
  height?: number
  backgroundClassName?: string
  foregroundClassName?: string
}

export default function ScrollProgress({
  containerRef,
  backgroundClassName = '',
  foregroundClassName = '',
  height = 5,
}: Props) {
  const [scrollProgress, setScrollProgress] = useState(0)
  const rafRef = useRef<number>(undefined)

  useEffect(() => {
    const handleScroll = () => {
      /**
       * Determine the target element to calculate scroll progress.
       * If a containerRef is provided, use that element; otherwise, use the document element.
       */
      const target = containerRef?.current || document.documentElement

      // Calculate the scroll progress based on the target element's scroll position
      const { scrollTop, scrollHeight, clientHeight } = target
      const totalHeight = scrollHeight - clientHeight
      const progress = (scrollTop / totalHeight) * 100

      // Update the scroll progress state
      setScrollProgress(progress)
    }

    // Throttle scroll handling to match display refresh rate ~60fps
    const onScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(handleScroll)
    }

    const target = containerRef?.current || window

    // Add a scroll event listener to the target element
    target.addEventListener('scroll', onScroll)

    return () => {
      // Remove the scroll event listener and last animation frame when the component is unmounted
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      target.removeEventListener('scroll', onScroll)
    }
  }, [containerRef])

  return (
    <div
      className="fixed z-9 w-full top-16 lg:top-25"
      data-test-id={SCROLL_PROGRESS_TEST_IDS.COMPONENT}
    >
      <div
        className={cn('left-0 w-full transition-colors ease-in', backgroundClassName, [
          scrollProgress > 0 ? 'bg-neutral-light-500' : 'bg-transparent',
        ])}
        style={{ height: `${height}px` }}
      />
      <div
        className={cn(
          'absolute top-0 left-0 w-full transition-colors ease-in',
          foregroundClassName,
          [scrollProgress > 0 ? 'bg-peach-600' : 'bg-transparent'],
        )}
        style={{ width: `${scrollProgress}%`, height: `${height}px` }}
        data-test-id={SCROLL_PROGRESS_TEST_IDS.PROGRESS}
      />
    </div>
  )
}
